// A vehicle's Journeys tab (interface refresh, screen 4): the day picker, the day's figures as tiles,
// a numbered list of journeys with the stops between them (parked shape, as on the live map), the
// picked journey's details, and the map with the same numbers. Same page in web/ and crm/.
import { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { Address } from '../components/Address';
import { icons } from '../components/Icons';
import { Link } from '../components/Links';
import { StateIcon } from '../components/VehicleState';
import { api, Day, Journey, Span, TrackPoint, Vehicle } from '../api';
import { FleetMap, MapRoute } from '../components/FleetMap';
import { VehicleHeader, VehicleTabs } from '../components/VehicleTabs';
import { SearchSelect } from '../components/SearchSelect';
import { dayLabel, duration, economy, irishDate, irishMidnight, km, kmh, litresText, shiftDate, time } from '../format';
import { useGeofences, useGeofencesShown } from '../components/geofenceStore';

type Row = { kind: 'journey'; j: Journey } | { kind: 'stop'; s: Span };

const ENDED_BY: Record<Journey['endedBy'], string> = {
  ignition: 'ignition off',
  stop: 'stopped',
  gap: 'signal lost',
  open: 'under way',
};
const ENDED_LONG: Record<Journey['endedBy'], string> = {
  ignition: 'Ended when the ignition went off.',
  stop: 'Ended when the vehicle stopped moving.',
  gap: 'Ended because the signal was lost.',
  open: 'Still under way.',
};
const NO_PLACES: { lat: number; lon: number }[] = [];
const sameStart = (a: string, b: string) => Math.abs(new Date(a).getTime() - new Date(b).getTime()) < 60_000;

// Like duration(), but a whole day stays in hours ("24 h", not "1 d 0 h").
const hours = (min: number) => min < 24 * 60 ? duration(min)
  : `${Math.floor(min / 60)} h${Math.round(min % 60) ? ` ${Math.round(min % 60)} min` : ''}`;

export function Journeys() {
  const fences = useGeofences();
  const [showFences] = useGeofencesShown();
  const params = useParams<{ id?: string; start?: string }>();
  const [search] = useSearchParams();
  const navigate = useNavigate();
  const [vehicles, setVehicles] = useState<Vehicle[]>([]);
  const [day, setDay] = useState<Day | null>(null);
  const [dayTrack, setDayTrack] = useState<TrackPoint[]>([]);
  // The picked journey's route, tagged with its start so a stale one is never shown.
  const [loadedRoute, setLoadedRoute] = useState<{ start: string; route: MapRoute } | null>(null);
  // "vehicle|date" once that day and its track have finished loading.
  const [dayDone, setDayDone] = useState('');
  const [error, setError] = useState('');
  const dateInput = useRef<HTMLInputElement>(null);

  const vehicleId = params.id ? Number(params.id) : vehicles[0]?.id;
  const start = params.start ?? null;
  const date = search.get('date') ?? (start ? irishDate(new Date(start)) : irishDate());
  const today = irishDate();
  const route = start && loadedRoute?.start === start ? loadedRoute.route : null;

  useEffect(() => {
    api.vehicles().then((r) => setVehicles(r.vehicles)).catch((e) => setError(e.message));
  }, []);

  // The day: journeys, stops and the whole day's track.
  useEffect(() => {
    if (!vehicleId) return;
    let cancelled = false;
    setDay(null);
    setDayTrack([]);
    api.day(vehicleId, date)
      .then(async (d) => {
        if (cancelled) return;
        setDay(d);
        setError('');
        if (d.journeys.length) {
          const t = await api.track(vehicleId, d.journeys[0].start, d.journeys[d.journeys.length - 1].end);
          if (!cancelled) setDayTrack(t.points);
        }
        if (!cancelled) setDayDone(`${vehicleId}|${date}`);
      })
      .catch((e) => !cancelled && setError(e.message));
    return () => { cancelled = true; };
  }, [vehicleId, date]);

  // One journey picked: its own route.
  useEffect(() => {
    if (!vehicleId || !start) return;
    let cancelled = false;
    api.journey(vehicleId, start)
      .then((j) => !cancelled && setLoadedRoute({ start, route: { points: j.track, from: j.from, to: j.to } }))
      .catch((e) => !cancelled && setError(e.message));
    return () => { cancelled = true; };
  }, [vehicleId, start]);

  const rows: Row[] = useMemo(() => {
    if (!day) return [];
    const list: Row[] = [
      ...day.journeys.map((j) => ({ kind: 'journey' as const, j })),
      ...day.stops.map((s) => ({ kind: 'stop' as const, s })),
    ];
    const at = (r: Row) => new Date(r.kind === 'journey' ? r.j.start : r.s.start).getTime();
    return list.sort((a, b) => at(a) - at(b));
  }, [day]);

  const dayRoute: MapRoute | null = useMemo(() => {
    if (!day?.journeys.length || dayTrack.length < 2) return null;
    return { points: dayTrack, from: day.journeys[0].from, to: day.journeys[day.journeys.length - 1].to };
  }, [day, dayTrack]);

  const go = (id: number | undefined, d: string, journeyStart?: string) => {
    if (!id) return;
    navigate(`/vehicles/${id}/journeys${journeyStart ? `/${encodeURIComponent(journeyStart)}` : ''}?date=${d}`);
  };

  // Time spent driving, idling and stopped within the day; spans over midnight count only their part on this day.
  const totals = useMemo(() => {
    if (!day) return null;
    const from = irishMidnight(date);
    const to = Math.min(irishMidnight(shiftDate(date, 1)), Date.now());
    const minutes = (spans: { start: string; end: string | null }[]) => spans.reduce((sum, s) => {
      const a = Math.max(new Date(s.start).getTime(), from);
      const b = Math.min(s.end ? new Date(s.end).getTime() : Date.now(), to);
      return sum + Math.max(0, b - a) / 60_000;
    }, 0);
    const driving = minutes(day.journeys.map((j) => ({ start: j.start, end: j.open ? null : j.end })));
    const stopped = minutes(day.stops);
    return { driving, idle: minutes(day.idles), stopped, total: driving + stopped };
  }, [day, date]);

  const selectedVehicle = vehicles.find((v) => v.id === vehicleId);
  // Only move the map once everything it should show has arrived.
  const mapReady = !!error || (dayDone === `${vehicleId}|${date}` && (!start || route !== null));
  const stopsOnMap = useMemo(() => (day?.stops ?? []).map((s) => ({ lat: s.lat, lon: s.lon })), [day]);
  const startsOnMap = useMemo(() => (day?.journeys ?? []).map((j) => j.from), [day]);
  const picked = start && day ? day.journeys.findIndex((j) => sameStart(j.start, start)) : -1;
  const pj = picked >= 0 ? day!.journeys[picked] : null;
  const dayJourneys = day ? day.journeys.filter((j) => irishDate(new Date(j.start)) === date).length : 0;
  const maxSpeed = day?.journeys.reduce<number | null>((m, j) => (j.maxSpeedKmh !== null && (m === null || j.maxSpeedKmh > m) ? j.maxSpeedKmh : m), null) ?? null;
  const dayName = date === today ? 'Today' : date === shiftDate(today, -1) ? 'Yesterday' : null;
  const openPicker = () => {
    const el = dateInput.current;
    if (!el) return;
    try { el.showPicker(); } catch { el.focus(); el.click(); }
  };

  return (
    <div className="split">
      <aside className="panel has-foot">
        <div className="panel-scroll">
          {vehicleId && <VehicleTabs vehicleId={vehicleId} active="journeys" />}
          {selectedVehicle && <VehicleHeader vehicle={selectedVehicle} />}
          <div className="panel-head jhead">
            {!selectedVehicle && <h1>Journeys</h1>}
            {!vehicleId && (
              <div className="toolbar">
                <label className="sr-only" htmlFor="vehicle">Vehicle</label>
                <SearchSelect id="vehicle" value="" onChange={(v) => go(Number(v), date)}
                  placeholder="Choose a vehicle…" options={vehicles.map((v) => ({ value: String(v.id), label: v.name }))} />
              </div>
            )}
            <div className="daybar" role="group" aria-label="Day">
              <button type="button" className="icon-btn" aria-label="Previous day" onClick={() => go(vehicleId, shiftDate(date, -1))}>{icons.collapse}</button>
              <div className="daypick">
                <button type="button" className="daypick-btn" onClick={openPicker} aria-label={`Day: ${dayLabel(`${date}T12:00:00Z`)}. Choose another day`}>
                  {icons.calendar}
                  <span>{dayName && <b>{dayName}</b>}<span className={dayName ? 'muted' : ''}>{dayLabel(`${date}T12:00:00Z`)}</span></span>
                </button>
                <input ref={dateInput} className="daypick-input" type="date" value={date} max={today} tabIndex={-1} aria-hidden="true"
                  onChange={(e) => e.target.value && go(vehicleId, e.target.value)} />
              </div>
              <button type="button" className="icon-btn" aria-label="Next day" disabled={date >= today} onClick={() => go(vehicleId, shiftDate(date, 1))}>{icons.expand}</button>
              {date !== today && <button type="button" className="button ghost small" onClick={() => go(vehicleId, today)}>Today</button>}
            </div>
            {error && <p className="error" role="alert">{error}</p>}
          </div>

          {pj ? (
            <section className="jdetail" aria-label="Journey details">
              <div className="jdetail-top">
                <button type="button" className="link small" onClick={() => go(vehicleId, date)}>← Whole day</button>
                <span className="muted small">Journey {picked + 1} of {day!.journeys.length}</span>
              </div>
              <h2>{time(pj.start)} – {pj.open ? 'now' : time(pj.end)}</h2>
              <dl className="vsum-tiles">
                <div><dt>Distance</dt><dd>{km(pj.distanceKm)}</dd></div>
                <div><dt>Duration</dt><dd>{duration(pj.durationMin)}</dd></div>
                <div><dt>Top speed</dt><dd>{kmh(pj.maxSpeedKmh)}</dd></div>
                <div><dt>Idling</dt><dd>{pj.idleMin ? duration(pj.idleMin) : 'none'}</dd></div>
                {pj.fuelL !== null && <div><dt>Fuel</dt><dd>{litresText(pj.fuelL)}</dd></div>}
                {pj.lPer100Km !== null && <div><dt>Economy</dt><dd>{economy(pj.lPer100Km)}</dd></div>}
              </dl>
              <dl className="jdetail-places">
                <dt>From</dt><dd><Address lat={pj.from.lat} lon={pj.from.lon} /></dd>
                <dt>{pj.open ? 'Now' : 'To'}</dt><dd><Address lat={pj.to.lat} lon={pj.to.lon} /></dd>
              </dl>
              <p className="muted small">{ENDED_LONG[pj.endedBy]}{irishDate(new Date(pj.start)) !== date ? ' Started the day before.' : ''}</p>
            </section>
          ) : day && rows.length > 0 && (
            <dl className="vsum-tiles tiles-3 jtiles" aria-label="This day">
              <div><dt>Distance</dt><dd>{km(day.totalKm)}</dd></div>
              <div><dt>Journeys</dt><dd>{dayJourneys}</dd></div>
              {day.totalFuelL !== null
                ? <div><dt>Fuel</dt><dd>{litresText(day.totalFuelL)}</dd>{day.lPer100Km !== null && <dd className="tile-sub">{economy(day.lPer100Km)}</dd>}</div>
                : <div><dt>Top speed</dt><dd>{kmh(maxSpeed)}</dd></div>}
            </dl>
          )}

          {!day && !error && <p className="muted pad">Loading…</p>}
          {day && rows.length === 0 && (
            <p className="muted pad">No journeys for {selectedVehicle?.name ?? 'this vehicle'} on {dayName ? dayName.toLowerCase() : dayLabel(`${date}T12:00:00Z`)}.</p>
          )}
          {rows.length > 0 && <h2 className="jlist-title">{pj ? 'All journeys this day' : 'Journeys and stops'}</h2>}
          <ol className="jlist">
            {rows.map((r) => {
              if (r.kind === 'stop') return (
                <li key={`s${r.s.start}`} className="jl-stop">
                  <span className="jl-mark"><StateIcon state="parked" size={18} /></span>
                  <span className="jl-body">
                    <span className="jl-top"><span>Parked {duration(r.s.durationMin)}</span>
                      <span className="jl-time">{time(r.s.start)}–{r.s.end ? time(r.s.end) : 'now'}</span></span>
                    <span className="jl-sub"><Address lat={r.s.lat} lon={r.s.lon} coords={false} /></span>
                  </span>
                </li>
              );
              const n = day!.journeys.indexOf(r.j) + 1;
              const on = !!start && sameStart(start, r.j.start);
              return (
                <li key={`j${r.j.start}`} className={`jl-journey${on ? ' on' : ''}`}>
                  <Link to={`/vehicles/${vehicleId}/journeys/${encodeURIComponent(r.j.start)}?date=${date}`} aria-current={on ? 'true' : undefined}>
                    <span className="jl-mark"><span className="jl-num">{n}</span></span>
                    <span className="jl-body">
                      <span className="jl-top">
                        <span className="jl-time-main">{time(r.j.start)} – {r.j.open ? 'now' : time(r.j.end)}</span>
                        <strong>{km(r.j.distanceKm)}</strong>
                      </span>
                      <span className="jl-sub">
                        {duration(r.j.durationMin)} · max {kmh(r.j.maxSpeedKmh)}{r.j.idleMin ? ` · idle ${duration(r.j.idleMin)}` : ''}
                        {r.j.fuelL !== null && <> · {litresText(r.j.fuelL)}{r.j.lPer100Km !== null && <> · {economy(r.j.lPer100Km)}</>}</>}
                        {' · '}{ENDED_BY[r.j.endedBy]}{irishDate(new Date(r.j.start)) !== date ? ' · from the day before' : ''}
                      </span>
                    </span>
                  </Link>
                </li>
              );
            })}
          </ol>
        </div>
        {totals && rows.length > 0 && (
          <footer className="day-total" aria-label="Time on this day">
            <div><span className="muted small">Driving</span><strong>{hours(totals.driving)}</strong>
              {totals.idle >= 1 && <span className="muted small">incl. idle {duration(totals.idle)}</span>}</div>
            <div><span className="muted small">Stopped</span><strong>{hours(totals.stopped)}</strong></div>
            <div><span className="muted small">Total</span><strong>{hours(totals.total)}</strong></div>
          </footer>
        )}
      </aside>
      <FleetMap route={route ?? dayRoute} stops={route ? NO_PLACES : stopsOnMap} numbered={route ? NO_PLACES : startsOnMap} fitKey={`${vehicleId}|${date}|${start ?? ''}|${route ? 'j' : dayRoute ? 'd' : '-'}`} ready={mapReady} geofences={fences} showGeofences={showFences} />
    </div>
  );
}
